/**
 * Anvilcore PDP interactions.
 *
 * Everything here uses event delegation attached once to `document`, so it
 * keeps working after the Shopify Theme Editor adds, removes, reorders, or
 * re-renders any of the anvilcore-* sections — no re-init step is required.
 * Variant selection itself needs no JS: plan cards are real
 * `<input type="radio" name="id">` elements inside the native product form,
 * so the browser handles selection/submission on its own. This file only
 * handles presentational state (active thumbnail, selected card styling,
 * quantity stepper).
 */
(function () {
  if (window.__acPdpInit) return;
  window.__acPdpInit = true;

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  // Shared helper: swap the gallery's main stage image, and (if a media id
  // is given) sync the matching thumbnail's active state. Used both when a
  // thumbnail is clicked directly and when a plan/variant selection changes
  // the current variant's image.
  function setStageImage(gallery, opts) {
    if (!gallery || !opts || !opts.src) return;
    var stageImg = gallery.querySelector('[data-ac-stage-img]');
    if (stageImg) {
      stageImg.src = opts.src;
      if (opts.srcset) {
        stageImg.srcset = opts.srcset;
      } else {
        stageImg.removeAttribute('srcset');
      }
      if (opts.alt) stageImg.alt = opts.alt;
    }

    if (opts.mediaId) {
      gallery.querySelectorAll('[data-ac-thumb]').forEach(function (t) {
        t.setAttribute('data-active', String(t.getAttribute('data-ac-media-id') === opts.mediaId));
      });
    }
  }

  // Gallery: click a thumbnail to swap the main stage image.
  document.addEventListener('click', function (event) {
    var thumb = closest(event.target, '[data-ac-thumb]');
    if (!thumb) return;
    var gallery = closest(thumb, '[data-ac-gallery]');
    if (!gallery) return;

    setStageImage(gallery, {
      src: thumb.getAttribute('data-ac-full'),
      srcset: thumb.getAttribute('data-ac-full-srcset'),
      alt: thumb.getAttribute('data-ac-alt'),
      mediaId: thumb.getAttribute('data-ac-media-id'),
    });
  });

  // Plan cards: reflect the checked radio's visual state, keep the real
  // hidden variant-id field (consumed by the native product form / theme
  // JS) in sync, and recompute the Add to Cart total.
  function formatMoney(scope, amount) {
    var currency = scope.getAttribute('data-ac-currency') || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
        trailingZeroDisplay: 'stripIfInteger',
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function syncPlanCards(scope) {
    var checked = null;
    scope.querySelectorAll('[data-ac-plan]').forEach(function (card) {
      var input = card.querySelector('input[type="radio"]');
      var isChecked = !!(input && input.checked);
      card.setAttribute('data-selected', String(isChecked));
      if (isChecked) checked = input;
    });

    if (!checked) return;

    var hiddenVariantInput = scope.querySelector('[data-ac-variant-input]');
    if (hiddenVariantInput && checked.getAttribute('data-variant-id')) {
      hiddenVariantInput.value = checked.getAttribute('data-variant-id');
      hiddenVariantInput.dispatchEvent(new Event('input', { bubbles: true }));
      hiddenVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Swap the gallery stage image to the selected variant's own image (if
    // it has one distinct from whatever is already showing).
    var gallery = scope.querySelector('[data-ac-gallery]');
    if (gallery && checked.getAttribute('data-image-src')) {
      setStageImage(gallery, {
        src: checked.getAttribute('data-image-src'),
        srcset: checked.getAttribute('data-image-srcset'),
        alt: checked.getAttribute('data-image-alt'),
        mediaId: checked.getAttribute('data-media-id'),
      });
    }

    var qtyInput = scope.querySelector('[data-ac-qty] input[type="number"]');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var priceValue = parseFloat(checked.getAttribute('data-price-value') || '0');
    var totalEl = scope.querySelector('[data-ac-total]');
    if (totalEl && !isNaN(priceValue)) {
      totalEl.textContent = formatMoney(scope, priceValue * qty);
    }
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (input.matches && input.matches('[data-ac-plan] input[type="radio"]')) {
      var group = closest(input, '[data-ac-buybox]');
      if (group) syncPlanCards(group);
      return;
    }
    if (input.matches && input.matches('[data-ac-qty] input[type="number"]')) {
      var buybox = closest(input, '[data-ac-buybox]');
      if (buybox) syncPlanCards(buybox);
    }
  });

  document.querySelectorAll('[data-ac-buybox]').forEach(syncPlanCards);

  // Quantity stepper: -/+ buttons drive the native number input.
  document.addEventListener('click', function (event) {
    var btn = closest(event.target, '[data-ac-qty-step]');
    if (!btn) return;
    var wrapper = closest(btn, '[data-ac-qty]');
    if (!wrapper) return;
    var input = wrapper.querySelector('input[type="number"]');
    if (!input) return;

    var step = parseInt(input.step || '1', 10) || 1;
    var min = parseInt(input.min || '1', 10) || 1;
    var max = input.max ? parseInt(input.max, 10) : Infinity;
    var current = parseInt(input.value || '1', 10) || 1;
    var next = btn.getAttribute('data-ac-qty-step') === 'up' ? current + step : current - step;
    next = Math.max(min, Math.min(max, next));

    if (next !== current) {
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // Re-sync plan cards whenever the Theme Editor swaps a section back in.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.querySelectorAll) {
      target.querySelectorAll('[data-ac-buybox]').forEach(syncPlanCards);
    }
  });
})();
